import { toast } from 'sonner';
import { Mic, Sparkles, Upload } from 'lucide-react';
import { type RefObject, useCallback, useState } from 'react';

import { cn } from '@/lib/utils';
import { useAudioUpload } from '@/lib/hooks';
import { Label } from '@/components/ui/label';
import { usePickerTemplates } from '@/features/templates/api/templates-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RecordingsList, type RecordingsListRef } from '@/components/audio/RecordingsList';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface RecordingPanelProps {
  /** Called whenever the template select changes. */
  onTemplateChange: (id: string) => void;
  /** Parent-owned recordings list ref so submit handlers can read recordings. */
  recordingsListRef: RefObject<RecordingsListRef>;
  /** Template category passed to ``usePickerTemplates``. Defaults to ``'document'``. */
  templateCategory?: 'document';
  /** Controlled template id. Empty string when nothing is selected. */
  templateId: string;
  /** Show the asterisk + validation hint on the template label. Default true. */
  templateRequired?: boolean;
}

/**
 * Reusable Template & Generation + Audio Files surface.
 *
 * Extracted from `NewEncounterPage` so both the chart-bound encounter form
 * and the patientless `QuickStartPage` (Plan 07) share a single recording
 * experience. The parent owns the recordings list ref so it can collect
 * recordings on submit; everything else (audio upload, drag state, template
 * dropdown) lives inside the panel.
 */
export function RecordingPanel({
  templateId,
  onTemplateChange,
  recordingsListRef,
  templateRequired = true,
  templateCategory = 'document',
}: RecordingPanelProps) {
  const [isDragging, setIsDragging] = useState(false);

  const { data: templatesData } = usePickerTemplates({ category: templateCategory });
  const templates = templatesData ?? [];

  const handleAudioUploadError = useCallback((error: string) => {
    toast.error(error);
  }, []);

  const handleAudioUploadSuccess = useCallback(
    (result: { blob: Blob; fileName: string; metadata: { duration: number } }) => {
      const newRecording = {
        blob: result.blob,
        timestamp: new Date(),
        name: result.fileName,
        id: crypto.randomUUID(),
        duration: result.metadata.duration,
      };
      recordingsListRef.current?.addRecording(newRecording);
    },
    [recordingsListRef],
  );

  const audioUpload = useAudioUpload({
    maxFileSize: 100 * 1024 * 1024,
    onError: handleAudioUploadError,
    onSuccess: handleAudioUploadSuccess,
  });

  const inputId = 'recording-panel-audio-file-input';

  return (
    <div className="space-y-6">
      {/* Template & Generation */}
      <Card className="border-border/60">
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
            Template &amp; Generation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">
              Template
              {templateRequired && <span className="ml-1 text-destructive">*</span>}
            </Label>
            <Select value={templateId} onValueChange={onTemplateChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select template..." />
              </SelectTrigger>
              <SelectContent>
                {templates
                  .filter((t) => t.is_active)
                  .map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Audio Files */}
      <Card className="border-border/60">
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Mic className="h-3.5 w-3.5" />
            </div>
            Audio Files
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            id={inputId}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) {
                await audioUpload.upload(file);
              }
            }}
          />
          <label
            htmlFor={inputId}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
            }}
            onDrop={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file && file.type.startsWith('audio/')) {
                await audioUpload.upload(file);
              } else if (file) {
                toast.error('Please upload an audio file');
              }
            }}>
            <div
              className={cn(
                'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-6 transition-all cursor-pointer',
                isDragging && 'border-primary bg-primary/5 scale-[1.01]',
                !isDragging && 'border-muted-foreground/20 hover:border-primary/40 hover:bg-muted/30',
              )}>
              <Upload
                className={cn('h-5 w-5 transition-colors', isDragging ? 'text-primary' : 'text-muted-foreground')}
              />
              <div className="text-center space-y-0.5">
                <p className="text-sm font-medium">{isDragging ? 'Drop audio file here' : 'Upload audio file'}</p>
                <p className="text-xs text-muted-foreground">Drag &amp; drop or click to browse</p>
              </div>
            </div>
          </label>

          <RecordingsList ref={recordingsListRef} />
        </CardContent>
      </Card>
    </div>
  );
}
